import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Remembers and restores the window scroll position across route changes,
 * keyed by route (default: the current pathname).
 *
 * Use on long list pages so navigating into a detail page and coming back
 * (via browser-back OR an in-app link to the same route) returns the user to
 * where they were instead of snapping to the top.
 *
 * The dashboard layout has a `fixed` sidebar and content in normal document
 * flow under a `min-h-screen` root, so the *window* is the scroll container
 * (the `<main overflow-y-auto>` never actually overflows). A module-level store
 * keeps offsets for the SPA session (cleared on a full refresh, as desired).
 */

const scrollPositions = new Map<string, number>();

export function useScrollRestoration(key?: string): void {
  const { pathname } = useLocation();
  const storageKey = key ?? pathname;

  useEffect(() => {
    const saved = scrollPositions.get(storageKey) ?? 0;

    // Restore after layout settles. Re-assert across a few frames in case the
    // page height isn't final yet, so a large offset isn't clamped to the top.
    let frame = 0;
    let attempts = 0;
    const restore = (): void => {
      window.scrollTo(0, saved);
      attempts += 1;
      if (saved > 0 && Math.abs(window.scrollY - saved) > 2 && attempts < 10) {
        frame = requestAnimationFrame(restore);
      }
    };
    frame = requestAnimationFrame(restore);

    // Continuously remember where the user is while this page is mounted.
    const handleScroll = (): void => {
      scrollPositions.set(storageKey, window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);
}
